import { FC } from 'react'

import { useStore } from '@tanstack/react-form'
import { formatCurrencyCent } from 'commons/utils'
import { FF, FieldSet, FormType, IconName, IconSize, Info, Tooltip, Validators } from 'ui/components'
import { CurrencyCentInput, TextareaInput } from 'ui/components/InputV2'
import { Col, Col2x, FormRow } from 'ui/themes/globalStyles'

import { BankSourceSetupForm } from '../../types'

import * as SC from './styles'

export const SalesDataSourceForm: FC<{ formApi: FormType<BankSourceSetupForm> }> = ({ formApi }) => {
  const totalCannabisSales = useStore(formApi.store, (state) => state.values.totalCannabisSales)
  const totalNonCannabisSales = useStore(formApi.store, (state) => state.values.totalNonCannabisSales)
  const totalSalesTaxCollected = useStore(formApi.store, (state) => state.values.totalSalesTaxCollected)

  return (
    <FieldSet>
      <FormRow>
        <Col>
          <SC.FieldWithIcon>
            <formApi.Field
              name="totalCannabisSales"
              validators={{
                onSubmit: ({ value }) => Validators.required.number(value.toString())
              }}
              children={(filesField) => (
                <FF
                  field={filesField}
                  label="Total Cannabis Sales Excluding Sales Tax"
                  input={(inputProps) => <CurrencyCentInput {...inputProps} data-qa="totalCannabisSales" />}
                />
              )}
            />
            <SC.IconWrapper>
              <Tooltip
                id="totalCannabisSales"
                type="dark"
                event="hover"
                content="Please enter the total amount of only cannabis product sales for the month in this area"
              >
                <SC.InfoIcon name={IconName.INFO} size={IconSize.S} />
              </Tooltip>
            </SC.IconWrapper>
          </SC.FieldWithIcon>
        </Col>
        <Col>
          <SC.FieldWithIcon>
            <formApi.Field
              name="totalNonCannabisSales"
              validators={{
                onSubmit: ({ value }) => Validators.required.number(value.toString())
              }}
              children={(filesField) => (
                <FF
                  field={filesField}
                  label="Total Non-Cannabis Sales"
                  input={(inputProps) => <CurrencyCentInput {...inputProps} data-qa="totalNonCannabisSales" />}
                />
              )}
            />
            <SC.IconWrapper>
              <Tooltip
                id="totalNonCannabisSales"
                type="dark"
                event="hover"
                content="Please enter the total amount of only non-cannabis sales for the month in this area"
              >
                <SC.InfoIcon name={IconName.INFO} size={IconSize.S} />
              </Tooltip>
            </SC.IconWrapper>
          </SC.FieldWithIcon>
        </Col>
      </FormRow>
      <FormRow>
        <Col>
          <SC.FieldWithIcon>
            <formApi.Field
              name="totalSalesTaxCollected"
              validators={{
                onSubmit: ({ value }) => Validators.required.number(value.toString())
              }}
              children={(filesField) => (
                <FF
                  field={filesField}
                  label="Estimated Total Sales Tax Collected"
                  input={(inputProps) => <CurrencyCentInput {...inputProps} data-qa="totalSalesTaxCollected" />}
                />
              )}
            />
            <SC.IconWrapper>
              <Tooltip
                id="totalSalesTaxCollected"
                type="dark"
                event="hover"
                content="Please enter the total sales tax amount for all sales (cannabis and non-cannabis sales during the
                month"
              >
                <SC.InfoIcon name={IconName.INFO} size={IconSize.S} />
              </Tooltip>
            </SC.IconWrapper>
          </SC.FieldWithIcon>
        </Col>
        <Col>
          <Info label="Total Sales">
            <SC.FieldWithIcon>
              {totalCannabisSales !== null && totalNonCannabisSales !== null && totalSalesTaxCollected !== null && (
                <>{formatCurrencyCent(totalCannabisSales + totalNonCannabisSales + totalSalesTaxCollected)}</>
              )}
              <Tooltip
                id="totalSales"
                type="dark"
                event="hover"
                content="Automatically calculated by summing previous categories"
              >
                <SC.InfoIcon name={IconName.INFO} size={IconSize.S} />
              </Tooltip>
            </SC.FieldWithIcon>
          </Info>
        </Col>
      </FormRow>
      <FormRow>
        <Col2x>
          <formApi.Field
            name="internalNotes"
            children={(filesField) => (
              <FF
                field={filesField}
                label="Internal Notes"
                input={(inputProps) => <TextareaInput {...inputProps} data-qa="internalNotes" />}
              />
            )}
          />
        </Col2x>
      </FormRow>
    </FieldSet>
  )
}
